import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { z } from 'zod/v3'

import type { WarehousePositionLotType } from '@/lib/api/warehouse-position-api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useDebounced } from '@/hooks/use-debounced'
import { useWarehousePositions } from '@/hooks/use-warehouse-position-query'
import { m } from '@/lib/paraglide/messages'

const searchSchema = z.object({
    page: z.coerce.number().int().min(1).catch(1),
    lot_type: z.enum(['product', 'packaging']).optional(),
    is_active: z.enum(['true', 'false']).optional(),
})

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/warehouse-positions/',
)({
    validateSearch: searchSchema,
    component: WarehousePositionsListPage,
})

function WarehousePositionsListPage() {
    const navigate = useNavigate()
    const search = Route.useSearch()

    const [searchInput, setSearchInput] = useState('')
    const debouncedSearch = useDebounced(searchInput, 300)

    const { data, isLoading } = useWarehousePositions({
        page: search.page,
        per_page: 20,
        lot_type: search.lot_type,
        is_active:
            search.is_active === undefined
                ? undefined
                : search.is_active === 'true',
        search: debouncedSearch || undefined,
    })

    return (
        <div className="space-y-4 p-4 pb-24 md:space-y-6 md:p-6">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold">
                    {m.warehouse_position_list_title()}
                </h1>
                <Link to="/warehouse-positions/new">
                    <Button size="sm">
                        <Plus className="size-4 mr-1" />
                        {m.warehouse_position_new_title()}
                    </Button>
                </Link>
            </div>

            <div className="grid gap-3 md:grid-cols-3 rounded-md border bg-card p-4">
                <div>
                    <Label>{m.warehouse_position_applicable_types()}</Label>
                    <Select
                        value={search.lot_type ?? 'all'}
                        onValueChange={(v) =>
                            void navigate({
                                to: '/warehouse-positions',
                                search: {
                                    ...search,
                                    page: 1,
                                    lot_type:
                                        v === 'all'
                                            ? undefined
                                            : (v as WarehousePositionLotType),
                                },
                            })
                        }
                    >
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">
                                {m.warehouse_position_filter_all()}
                            </SelectItem>
                            <SelectItem value="product">
                                {m.warehouse_position_type_product()}
                            </SelectItem>
                            <SelectItem value="packaging">
                                {m.warehouse_position_type_packaging()}
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label>{m.warehouse_position_status_label()}</Label>
                    <Select
                        value={search.is_active ?? 'all'}
                        onValueChange={(v) =>
                            void navigate({
                                to: '/warehouse-positions',
                                search: {
                                    ...search,
                                    page: 1,
                                    is_active:
                                        v === 'all'
                                            ? undefined
                                            : (v as 'true' | 'false'),
                                },
                            })
                        }
                    >
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">
                                {m.warehouse_position_filter_all()}
                            </SelectItem>
                            <SelectItem value="true">
                                {m.warehouse_position_active()}
                            </SelectItem>
                            <SelectItem value="false">
                                {m.warehouse_position_inactive()}
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label>{m.warehouse_position_code()}</Label>
                    <Input
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder={m.warehouse_position_code_placeholder()}
                    />
                </div>
            </div>

            <div className="overflow-x-auto rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.warehouse_position_code()}</TableHead>
                            <TableHead>{m.warehouse_position_name()}</TableHead>
                            <TableHead>
                                {m.warehouse_position_applicable_types()}
                            </TableHead>
                            <TableHead>
                                {m.warehouse_position_status_label()}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading && (
                            <TableRow>
                                <TableCell colSpan={4}>
                                    {m.warehouse_position_loading()}
                                </TableCell>
                            </TableRow>
                        )}
                        {!isLoading && (data?.data ?? []).length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4}>
                                    {m.warehouse_position_empty()}
                                </TableCell>
                            </TableRow>
                        )}
                        {(data?.data ?? []).map((p) => (
                            <TableRow
                                key={p.id}
                                role="button"
                                className="cursor-pointer hover:bg-muted/50"
                                onClick={() =>
                                    void navigate({
                                        to: '/warehouse-positions/$id',
                                        params: { id: p.id },
                                    })
                                }
                            >
                                <TableCell className="font-mono">
                                    {p.code}
                                </TableCell>
                                <TableCell>{p.name ?? '—'}</TableCell>
                                <TableCell>
                                    <div className="flex gap-1 flex-wrap">
                                        {p.applicable_lot_types.map((t) => (
                                            <Badge key={t} variant="outline">
                                                {t === 'product'
                                                    ? m.warehouse_position_type_product()
                                                    : m.warehouse_position_type_packaging()}
                                            </Badge>
                                        ))}
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <Badge
                                        variant={
                                            p.is_active
                                                ? 'default'
                                                : 'destructive'
                                        }
                                    >
                                        {p.is_active
                                            ? m.warehouse_position_active()
                                            : m.warehouse_position_inactive()}
                                    </Badge>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    )
}
